
import React, { useEffect, useState } from 'react';
import { Building2, ArrowRight, BarChart3, Bot, ShieldCheck, Landmark, CheckCircle2, LayoutDashboard, Map, TrendingUp, Globe, Lock, ArrowUpRight, Activity, Link, Zap, CreditCard, FileText, Layers } from 'lucide-react';
import { motion, Variants } from 'framer-motion';

interface LandingPageProps {
  onNavigateToAuth: () => void;
}

const LandingPage: React.FC<LandingPageProps> = ({ onNavigateToAuth }) => {
  // Demo mode removed for cleaner SaaS

  
  const fadeInUp: Variants = {
    hidden: { opacity: 0, y: 40, filter: 'blur(4px)' },
    visible: { 
        opacity: 1, 
        y: 0, 
        filter: 'blur(0px)',
        transition: { duration: 0.8, ease: [0.22, 1, 0.36, 1] as const } 
    }
  };

  const staggerContainer: Variants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.15,
        delayChildren: 0.1
      }
    }
  };

  const scaleIn: Variants = {
    hidden: { scale: 0.9, opacity: 0 },
    visible: { 
        scale: 1, 
        opacity: 1,
        transition: { duration: 0.6, ease: "easeOut" }
    }
  };

  const handleScroll = (e: React.MouseEvent<HTMLAnchorElement>, id: string) => {
    e.preventDefault();
    const element = document.getElementById(id);
    if (element) {
      const offset = 80; // height of sticky header
      const bodyRect = document.body.getBoundingClientRect().top;
      const elementRect = element.getBoundingClientRect().top;
      const elementPosition = elementRect - bodyRect;
      const offsetPosition = elementPosition - offset;

      window.scrollTo({
        top: offsetPosition,
        behavior: 'smooth'
      });
    }
  };

  const generateSystemLog = () => {
    const { jsPDF } = require('jspdf');
    const doc = new jsPDF();
    const pageWidth = doc.internal.pageSize.getWidth();
    const pageHeight = doc.internal.pageSize.getHeight();
    const margin = 20;
    const contentWidth = pageWidth - (margin * 2);
    
    let yPos = 20;

    // Helper: Add Text with auto-paging
    const addLine = (text: string, fontSize: number = 10, isBold: boolean = false, color: string = '#334155', indent: number = 0) => {
        doc.setFontSize(fontSize);
        doc.setFont("helvetica", isBold ? "bold" : "normal");
        doc.setTextColor(color);
        
        const availableWidth = contentWidth - indent;
        const lines = doc.splitTextToSize(text, availableWidth);
        
        // Check Page Break
        if (yPos + (lines.length * (fontSize * 0.4)) > pageHeight - margin) {
            doc.addPage();
            yPos = 20;
            // Add Header on new page
            doc.setFontSize(8);
            doc.setTextColor('#cbd5e1');
            doc.text("DC PropChain // System Status Log // Confidential", margin, 10);
        }

        doc.text(lines, margin + indent, yPos);
        yPos += (lines.length * (fontSize * 0.4)) + 2; // Line height
    };

    const addDivider = () => {
        yPos += 2;
        doc.setDrawColor(226, 232, 240); // slate-200
        doc.line(margin, yPos, pageWidth - margin, yPos);
        yPos += 8;
    }

    // --- DOCUMENT CONTENT START ---

    // 1. HEADER
    doc.setFillColor(2, 6, 23); // Slate 950
    doc.rect(0, 0, pageWidth, 40, 'F');
    
    doc.setTextColor(16, 185, 129); // Emerald 500
    doc.setFontSize(22);
    doc.setFont("helvetica", "bold");
    doc.text("DC PropChain", margin, 20);
    
    doc.setTextColor(255, 255, 255);
    doc.setFontSize(12);
    doc.text("System Status Log & Technical Specification", margin, 30);
    
    yPos = 50;

    // 2. METADATA
    addLine("SYSTEM METADATA", 10, true, '#94a3b8');
    addLine(`Generated: ${new Date().toISOString()}`, 10, false);
    addLine("Version: v2.5.0-beta", 10, false);
    addLine("Environment: Production / Client-Side Runtime", 10, false);
    addLine("Engine: React 19 + TypeScript + Google Gemini 1.5 Flash", 10, false);
    addDivider();

    // 3. EXECUTIVE SUMMARY
    addLine("1.0 EXECUTIVE SUMMARY", 14, true, '#0f172a');
    yPos += 2;
    addLine("DC PropChain is a vertical SaaS Operating System architected for the Portuguese Real Estate Investment market. The system functions as a Progressive Web Application (PWA) with offline-first capabilities, utilizing local storage persistence strategies to ensure data sovereignty and zero-latency operations. It integrates a multi-tiered subscription model regulating access to advanced financial engines and AI modules.", 10, false);
    addDivider();

    // 4. ACCESS CONTROL & MONETIZATION
    addLine("2.0 ACCESS CONTROL & SUBSCRIPTION LOGIC", 14, true, '#0f172a');
    yPos += 2;
    addLine("The application implements a strict Role-Based Access Control (RBAC) system tied to subscription tiers:", 10, false);
    yPos += 4;

    addLine("2.1 STARTER TIER (Free)", 11, true, '#059669');
    addLine("• Limit: 1 Property Asset.", 10, false, '#475569', 5);
    addLine("• Restriction: AI Advisor, Deal Flow, and Analytics modules are hard-locked.", 10, false, '#475569', 5);
    addLine("• Purpose: Entry-level evaluation and single-unit tracking.", 10, false, '#475569', 5);

    yPos += 4;
    addLine("2.2 INVESTOR TIER (€15/mo)", 11, true, '#059669');
    addLine("• Limit: 10 Property Assets.", 10, false, '#475569', 5);
    addLine("• Features Unlocked: Deal Flow Pipeline (Kanban), Renovation Project Management, Smart Alerts.", 10, false, '#475569', 5);
    addLine("• AI Access: Standard Gemini 2.5 Flash context window for advisory.", 10, false, '#475569', 5);

    yPos += 4;
    addLine("2.3 PRO TIER (€49/mo)", 11, true, '#059669');
    addLine("• Limit: Unlimited Assets.", 10, false, '#475569', 5);
    addLine("• Features Unlocked: Advanced Scenario Simulator (Hold vs Sell), White-label Reporting.", 10, false, '#475569', 5);
    addLine("• Priority: High-priority processing for AI requests.", 10, false, '#475569', 5);
    addDivider();

    // 5. MODULE SPECIFICATIONS
    addLine("3.0 MODULE SPECIFICATIONS & ALGORITHMS", 14, true, '#0f172a');
    yPos += 2;

    addLine("3.1 ASSET MANAGEMENT CORE", 11, true);
    addLine("Architecture: CRUD matrix with dual-view rendering (Grid/Leaflet Map).", 10, false, '#475569');
    addLine("Data Points: Purchase Price, Loan Amount (LTV), Current Valuation, Monthly Rent, OpEx.", 10, false, '#475569');
    addLine("Logic: Automatic Net Equity calculation (Valuation - Debt). Real-time status tracking (Occupied/Vacant/Maintenance) with visual semaphore indicators.", 10, false, '#475569');
    yPos += 4;

    addLine("3.2 FINANCIAL ENGINE (PORTUGAL LOCALIZATION)", 11, true);
    addLine("IMT Calculator: Implements 2024 Continental Portugal tax brackets. Differentiates between Primary Residence (HPP) and Secondary/Investment purposes.", 10, false, '#475569');
    addLine("Stamp Duty (Imposto Selo): Fixed rate application (0.8%) on deed value.", 10, false, '#475569');
    addLine("Deal Analyzer: Computes NOI (Net Operating Income), Cap Rate, and Cash-on-Cash return instantly upon data entry.", 10, false, '#475569');
    yPos += 4;

    addLine("3.3 CONSTRUCTION & RENOVATION MANAGER", 11, true);
    addLine("Gantt Logic: Dynamic timeline rendering based on task start/end dates.", 10, false, '#475569');
    addLine("Budget Tracking: Real-time variance analysis (Estimated vs Actual Cost).", 10, false, '#475569');
    addLine("AI Estimator: Integration with Gemini API to generate cost ranges (Low/Avg/High) based on sqm and location inputs.", 10, false, '#475569');
    addLine("Quote Comparison: Normalized view to compare multiple contractor bids side-by-side.", 10, false, '#475569');
    yPos += 4;

    addLine("3.4 STRATEGY SIMULATOR (PRO)", 11, true);
    addLine("Scenario A (Sell): Calculates Capital Gains Tax (Mais-Valias) considering inflation coefficients (simplified) and ownership type (Individual vs Company).", 10, false, '#475569');
    addLine("Scenario B (Hold): Projects 5-10 year Cashflow + Appreciation Compound Annual Growth Rate (CAGR).", 10, false, '#475569');
    addLine("Scenario C (Refinance): Analyzes debt service coverage ratio (DSCR) impact post-refinance.", 10, false, '#475569');
    yPos += 4;

    addLine("3.5 ANALYTICS COCKPIT", 11, true);
    addLine("Visualization: Recharts implementation for 12-month Revenue vs Expense trend lines.", 10, false, '#475569');
    addLine("Heuristics: Auto-ranking algorithm identifies Top 3 and Bottom 3 performing assets based on Net Monthly Cashflow.", 10, false, '#475569');
    addLine("Smart Alerts: Algorithmic detection of 'Low Cap Rate' (<3%) and 'High Vacancy' risks.", 10, false, '#475569');
    addDivider();

    // 6. TECHNICAL INFRASTRUCTURE
    addLine("4.0 TECHNICAL INFRASTRUCTURE", 14, true, '#0f172a');
    yPos += 2;
    addLine("Frontend: React 19 with Framer Motion for hardware-accelerated transitions.", 10, false, '#475569');
    addLine("Styling: Tailwind CSS using a 'Gotham' dark-mode design system (Slate-950 base).", 10, false, '#475569');
    addLine("Persistence: StorageService abstraction layer handling localStorage serialization/deserialization with error boundaries.", 10, false, '#475569');
    addLine("AI Layer: Google GenAI SDK (@google/genai) integration. System prompts utilize specific personas (Portuguese Investment Advisor, Quantity Surveyor).", 10, false, '#475569');
    addLine("Security: Simulated JWT-style authentication flow. Data remains client-side (GDPR compliant by design).", 10, false, '#475569');
    addDivider();

    // 7. UI/UX LOG
    addLine("5.0 INTERFACE & UX LOG", 14, true, '#0f172a');
    yPos += 2;
    addLine("Header: Expanded height (h-24) for better visual weight. Includes global User Profile and System Status indicators.", 10, false, '#475569');
    addLine("Sidebar: Collapsible navigation. Subscription widget located at footer. Zoom controls integrated into Profile settings.", 10, false, '#475569');
    addLine("Responsiveness: Fully adaptive grid layouts (1col mobile -> 4col desktop).", 10, false, '#475569');

    // FOOTER
    doc.setFontSize(8);
    doc.setTextColor('#94a3b8');
    doc.text("End of System Log // Generated by DC PropChain Core", margin, pageHeight - 10);

    doc.save("DC_PropChain_System_Log_v2.5.pdf");
  };

  return (
    <div className="min-h-screen bg-slate-950 font-sans text-slate-300 selection:bg-emerald-500/30 selection:text-emerald-200 flex flex-col overflow-x-hidden relative">
      
      {/* --- ARCHITECTURAL BLUEPRINT BACKGROUND --- */}
      <div className="fixed inset-0 pointer-events-none z-0 overflow-hidden">
        {/* 1. Base Grid Pattern */}
        <div className="absolute inset-0 opacity-[0.03]" 
             style={{ 
                 backgroundImage: 'linear-gradient(#94a3b8 1px, transparent 1px), linear-gradient(90deg, #94a3b8 1px, transparent 1px)', 
                 backgroundSize: '40px 40px' 
             }}>
        </div>

        {/* 2. Floor Plan Lines (Abstract Top-Down) */}
        <svg className="absolute top-0 left-0 w-full h-full opacity-[0.08] stroke-slate-500 stroke-1 fill-none">
            {/* Room Outlines - Left Side */}
            <path d="M-100,100 L300,100 L300,400 L-100,400 Z" />
            <path d="M300,100 L600,100 L600,250 L300,250" />
            <path d="M150,100 L150,250" strokeDasharray="5,5" />
            
            {/* Room Outlines - Right Side */}
            <path d="M1000,50 L1400,50 L1400,350 L1000,350 Z" />
            <path d="M1200,50 L1200,350" />
            <path d="M1000,200 L1400,200" />
            <circle cx="1100" cy="125" r="30" strokeDasharray="4,4" />

            {/* Door Arcs */}
            <path d="M300,150 Q330,150 330,180" />
            <path d="M1200,250 Q1170,250 1170,220" />
        </svg>

        {/* 3. Isometric 3D Wireframe Unit (Floating Hero Graphic) */}
        <div className="absolute top-[15%] right-[-5%] lg:right-[5%] w-[600px] h-[600px] opacity-[0.15] hidden lg:block">
             <svg viewBox="0 0 500 500" className="w-full h-full stroke-emerald-500 stroke-[0.5] fill-none drop-shadow-[0_0_15px_rgba(16,185,129,0.3)]">
                 {/* Isometric Cube / House Shape */}
                 <g transform="translate(250, 100)">
                    {/* Roof/Top */}
                    <path d="M0,0 L150,75 L0,150 L-150,75 Z" fill="rgba(16,185,129,0.05)" />
                    
                    {/* Left Face */}
                    <path d="M-150,75 L0,150 L0,350 L-150,275 Z" fill="rgba(16,185,129,0.02)" />
                    
                    {/* Right Face */}
                    <path d="M0,150 L150,75 L150,275 L0,350 Z" fill="rgba(16,185,129,0.08)" />
                    
                    {/* Internal Floors */}
                    <path d="M-150,175 L0,250 L150,175" strokeDasharray="5,5" />
                    <path d="M0,150 L0,350" />

                    {/* Data Points / Smart Nodes */}
                    <circle cx="-75" cy="112" r="3" fill="#10b981" />
                    <circle cx="75" cy="112" r="3" fill="#10b981" />
                    <circle cx="0" cy="250" r="3" fill="#10b981" />
                    
                    {/* Floating Labels */}
                    <text x="-160" y="80" className="text-[10px] fill-emerald-400 font-mono">MASTER SUITE</text>
                    <text x="160" y="180" className="text-[10px] fill-emerald-400 font-mono">LIVING AREA</text>
                 </g>
             </svg>
        </div>

        {/* Ambient Glow Gradients */}
        <div className="absolute inset-0 bg-gradient-to-b from-slate-950/80 via-slate-950/90 to-slate-950" />
        <motion.div 
            animate={{ opacity: [0.2, 0.4, 0.2] }}
            transition={{ duration: 8, repeat: Infinity, ease: "easeInOut" }}
            className="absolute top-[-10%] right-[10%] w-[500px] h-[500px] bg-emerald-900/20 rounded-full blur-[100px]"
        />
      </div>

      {/* Navigation */}
      <motion.nav 
        initial={{ y: -20, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        transition={{ duration: 0.5, delay: 0.2 }}
        className="sticky top-0 z-50 bg-slate-950/80 backdrop-blur-md border-b border-slate-800/50"
      >
        <div className="max-w-7xl mx-auto px-6 h-20 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-emerald-500/10 rounded-lg border border-emerald-500/20">
                <Link className="w-6 h-6 text-emerald-400" />
            </div>
            <span className="text-xl font-black tracking-tight text-white">DC<span className="text-emerald-500">PropChain</span></span>
          </div>
          
          <div className="hidden md:flex items-center gap-8 text-sm font-medium text-slate-400">
            <a href="#features" onClick={(e) => handleScroll(e, 'features')} className="hover:text-emerald-400 transition-colors cursor-pointer">Features</a>
            <a href="#market-data" onClick={(e) => handleScroll(e, 'market-data')} className="hover:text-emerald-400 transition-colors cursor-pointer">Market Data</a>
            <a href="#pricing" onClick={(e) => handleScroll(e, 'pricing')} className="hover:text-emerald-400 transition-colors cursor-pointer">Pricing</a>
          </div>
          
          <div className="flex gap-4">
            <button 
                onClick={onNavigateToAuth}
                className="text-slate-300 font-medium hover:text-white transition-colors"
            >
                Sign In
            </button>
            <button 
                onClick={onNavigateToAuth}
                className="bg-emerald-600 text-white px-5 py-2.5 rounded-lg text-sm font-bold hover:bg-emerald-500 transition-all shadow-[0_0_20px_rgba(16,185,129,0.3)] border border-emerald-500/50 flex items-center gap-2 group"
            >
                Get Started
                <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
            </button>
          </div>
        </div>
      </motion.nav>

      {/* Hero Section */}
      <motion.header 
        initial="hidden"
        whileInView="visible"
        viewport={{ once: false, margin: "-100px" }}
        variants={staggerContainer}
        className="relative pt-24 pb-32 lg:pt-40 lg:pb-48 flex-1 flex items-center z-10"
      >
        <div className="max-w-7xl mx-auto px-6 grid lg:grid-cols-2 gap-20 items-center">
          <motion.div variants={staggerContainer} className="space-y-8 max-w-2xl">
            <motion.div variants={fadeInUp} className="inline-flex items-center gap-3 px-4 py-1.5 rounded-full bg-slate-900/50 border border-slate-700/50 text-emerald-400 text-xs font-bold uppercase tracking-wider shadow-lg backdrop-blur-sm">
              <Layers className="w-3 h-3" />
              <span className="relative flex h-2 w-2">
                <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-75"></span>
                <span className="relative inline-flex rounded-full h-2 w-2 bg-emerald-500"></span>
              </span>
              Architectural OS v2.5.0
            </motion.div>
            
            <motion.h1 variants={fadeInUp} className="text-5xl lg:text-7xl font-black text-white leading-[1.1] tracking-tight">
              The Operating System for <br/>
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-emerald-400 via-teal-400 to-cyan-500 animate-gradient">
                Real Estate Assets
              </span>
            </motion.h1>

            <motion.p variants={fadeInUp} className="text-xl text-white font-medium">
              Linking properties, performance, and people.
            </motion.p>
            
            <motion.p variants={fadeInUp} className="text-lg text-slate-400 leading-relaxed border-l-2 border-emerald-500/30 pl-6">
              DC PropChain replaces spreadsheets with a professional-grade terminal. Track portfolios, calculate Portuguese taxes (IMI/IMT), and leverage AI for market analysis.
            </motion.p>
            
            <motion.div variants={fadeInUp} className="flex flex-col sm:flex-row gap-4 pt-4">
                 <button 
                    onClick={onNavigateToAuth}
                    className="bg-white text-slate-950 px-8 py-4 rounded-xl font-bold text-lg hover:bg-emerald-50 transition-all shadow-[0_0_30px_rgba(255,255,255,0.1)] flex items-center justify-center gap-2 transform hover:-translate-y-1"
                 >
                    Launch App
                    <ArrowRight className="w-5 h-5" />
                 </button>
                 <button 
                    onClick={onNavigateToAuth}
                    className="px-8 py-3 bg-slate-900/80 hover:bg-slate-800 text-white rounded-xl font-bold flex items-center justify-center gap-2 transition-all border border-slate-700 shadow-lg"
                 >
                    Get Started
                 </button>
            </motion.div>

            <motion.div variants={fadeInUp} className="flex items-center gap-4 text-xs font-mono text-slate-500 pt-6">
              <div className="flex -space-x-3">
                {[1,2,3,4].map(i => (
                    <div key={i} className="w-8 h-8 rounded-full bg-slate-800 border border-slate-950 flex items-center justify-center text-[10px] text-white font-bold">
                        {String.fromCharCode(64+i)}
                    </div>
                ))}
              </div>
              <p className="uppercase tracking-widest">Trusted by 500+ Investors</p>
            </motion.div>
          </motion.div>
          
          {/* Hero Graphic / Glass UI */}
          <motion.div variants={scaleIn} className="relative hidden lg:block perspective-1000">
             <div className="absolute -inset-10 bg-gradient-to-r from-emerald-500 to-cyan-500 rounded-full opacity-20 blur-3xl animate-pulse"></div>
             
             <motion.div 
                whileHover={{ rotateY: 0, rotateX: 0 }}
                initial={{ rotateY: 12, rotateX: 6 }}
                transition={{ type: "spring", stiffness: 100, damping: 30 }}
                className="relative bg-slate-950/80 backdrop-blur-xl rounded-2xl shadow-2xl border border-slate-800 p-2 transform transition-all duration-700 ease-out"
             >
                {/* Window Controls */}
                <div className="h-10 bg-slate-900/50 border-b border-slate-800 flex items-center px-4 gap-2 rounded-t-xl">
                    <div className="w-3 h-3 rounded-full bg-red-500/20 border border-red-500/50"></div>
                    <div className="w-3 h-3 rounded-full bg-amber-500/20 border border-amber-500/50"></div>
                    <div className="w-3 h-3 rounded-full bg-emerald-500/20 border border-emerald-500/50"></div>
                </div>
                
                {/* Mock Dashboard Content */}
                <div className="p-6 space-y-6 bg-slate-900 rounded-b-xl">
                    {/* KPI Row */}
                    <div className="grid grid-cols-2 gap-4">
                        <div className="bg-slate-900/50 p-4 rounded-xl border border-slate-800 relative overflow-hidden">
                            <div className="absolute top-0 right-0 w-16 h-16 bg-emerald-500/10 blur-xl rounded-full"></div>
                            <p className="text-xs text-slate-500 font-bold uppercase">Total Portfolio Value</p>
                            <p className="text-2xl font-bold text-white mt-1">€1,240,500</p>
                            <div className="mt-2 flex items-center text-xs text-emerald-400 gap-1 bg-emerald-500/10 w-fit px-2 py-0.5 rounded">
                                <TrendingUp className="w-3 h-3" /> +4.2%
                            </div>
                        </div>
                        <div className="bg-slate-900/50 p-4 rounded-xl border border-slate-800">
                            <p className="text-xs text-slate-500 font-bold uppercase">Monthly Cashflow</p>
                            <p className="text-2xl font-bold text-white mt-1">€8,350</p>
                            <div className="w-full bg-slate-800 h-1 mt-3 rounded-full overflow-hidden">
                                <div className="w-[75%] bg-blue-500 h-full"></div>
                            </div>
                        </div>
                    </div>

                    {/* List Row */}
                    <div className="space-y-3">
                        <div className="flex items-center justify-between text-xs text-slate-500 uppercase font-bold px-2">
                            <span>Asset</span>
                            <span>Status</span>
                        </div>
                        {[1, 2, 3].map((_, i) => (
                            <motion.div 
                                key={i}
                                initial={{ opacity: 0, x: -20 }}
                                animate={{ opacity: 1, x: 0 }}
                                transition={{ delay: 0.5 + (i * 0.1) }}
                                className="flex items-center justify-between p-3 bg-slate-900 rounded-lg border border-slate-800/50"
                            >
                                <div className="flex items-center gap-3">
                                    <div className="w-8 h-8 rounded bg-slate-800 flex items-center justify-center">
                                        <Building2 className="w-4 h-4 text-slate-600" />
                                    </div>
                                    <div>
                                        <div className="w-24 h-2 bg-slate-700 rounded mb-1"></div>
                                        <div className="w-16 h-1.5 bg-slate-800 rounded"></div>
                                    </div>
                                </div>
                                <div className="w-16 h-6 bg-emerald-500/10 rounded-full border border-emerald-500/20"></div>
                            </motion.div>
                        ))}
                    </div>
                </div>
             </motion.div>
          </motion.div>
        </div>
      </motion.header>

      {/* Features Section */}
      <section id="features" className="py-24 bg-slate-950/80 relative overflow-hidden">
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_center,rgba(16,185,129,0.05),transparent_70%)]"></div>
        
        <motion.div 
            initial="hidden"
            whileInView="visible"
            viewport={{ once: false, margin: "-50px" }}
            variants={staggerContainer}
            className="max-w-7xl mx-auto px-6 relative z-10"
        >
          <div className="text-center max-w-3xl mx-auto mb-20">
            <motion.h2 variants={fadeInUp} className="text-3xl md:text-5xl font-black text-white mb-6 tracking-tight">
                Intelligence for the <br />
                <span className="text-emerald-400">Modern Investor</span>
            </motion.h2>
            <motion.p variants={fadeInUp} className="text-lg text-slate-400">
                Replace spreadsheets with a command center built for property intelligence.
            </motion.p>
          </div>

          <motion.div variants={staggerContainer} className="grid md:grid-cols-3 gap-8">
            <FeatureCard 
              icon={BarChart3}
              title="Portfolio Analytics"
              desc="Real-time Cap Rate, ROI, and cash flow for all your units."
              color="blue"
              variants={fadeInUp}
            />
            <FeatureCard 
              icon={Landmark}
              title="Portuguese Tax Engine"
              desc="Built-in IMI and IMT calculators updated for 2024 regulations. Estimate your costs accurately."
              color="amber"
              variants={fadeInUp}
            />
            <FeatureCard 
              icon={Bot}
              title="AI Advisor"
              desc="Conversational AI for strategy, compliance, and market queries."
              color="emerald"
              variants={fadeInUp}
            />
            <FeatureCard 
              icon={Lock}
              title="Bank-Grade Security"
              desc="Secure storage and authentication of investor data. Your financial data is private and protected."
              color="red"
              variants={fadeInUp}
            />
             <FeatureCard 
              icon={CheckCircle2}
              title="Maintenance Operations"
              desc="Track repairs, costs, and manage contractor schedules easily."
              color="purple"
              variants={fadeInUp}
            />
             <FeatureCard 
              icon={TrendingUp}
              title="Deal Analyzer"
              desc="Instantly estimate returns on new opportunities. Input price and rent to see projected returns immediately."
              color="cyan"
              variants={fadeInUp}
            />
          </motion.div>
        </motion.div>
      </section>

      {/* Market Insight Strip */}
      <MarketIntelligenceSection />

      {/* Pricing Section */}
      <PricingSection onNavigateToAuth={onNavigateToAuth} />

      {/* Footer */}
      <footer className="bg-slate-950 pt-20 pb-10 border-t border-slate-900 relative z-10">
        <motion.div 
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            transition={{ duration: 1 }}
            viewport={{ once: false }}
            className="max-w-7xl mx-auto px-6"
        >
          <div className="grid md:grid-cols-4 gap-12 mb-16">
            <div className="col-span-1 md:col-span-2">
              <div className="flex items-center gap-2 text-white mb-6">
                <div className="p-1.5 bg-emerald-500/10 rounded border border-emerald-500/20">
                    <Link className="w-5 h-5 text-emerald-400" />
                </div>
                <span className="text-xl font-bold tracking-tight">DC PropChain</span>
              </div>
              <p className="text-slate-500 max-w-sm leading-relaxed">
                Empowering individual investors to build and manage wealth through Portuguese real estate with professional-grade technology.
              </p>
            </div>
            <div>
                <h4 className="text-white font-bold mb-6">Platform</h4>
                <ul className="space-y-3 text-sm text-slate-400">
                    <li><a href="#" className="hover:text-emerald-400 transition-colors">Portfolio Tracking</a></li>
                    <li><a href="#" className="hover:text-emerald-400 transition-colors">Tax Calculators</a></li>
                    <li><a href="#" className="hover:text-emerald-400 transition-colors">Investment AI</a></li>
                    <li><a href="#" className="hover:text-emerald-400 transition-colors">Deal Analyzer</a></li>
                </ul>
            </div>
            <div>
                <h4 className="text-white font-bold mb-6">Resources</h4>
                <ul className="space-y-3 text-sm text-slate-400">
                    <li>
                        <button 
                            onClick={generateSystemLog}
                            className="hover:text-emerald-400 transition-colors flex items-center gap-2"
                        >
                            <FileText className="w-3 h-3" /> System Resume (PDF)
                        </button>
                    </li>
                    <li><a href="#" className="hover:text-emerald-400 transition-colors">Market Guides</a></li>
                    <li><a href="#" className="hover:text-emerald-400 transition-colors">Privacy Policy</a></li>
                    <li><a href="#" className="hover:text-emerald-400 transition-colors">Terms of Service</a></li>
                </ul>
            </div>
          </div>
          <div className="pt-8 border-t border-slate-900 flex flex-col md:flex-row justify-between items-center gap-4 text-xs text-slate-600 font-mono">
            <p>© 2025 DC PropChain, by Diogo Carracinha. All rights reserved.</p>
            <p>Lisbon • Porto • Algarve</p>
          </div>
        </motion.div>
      </footer>
    </div>
  );
};

// --- Sub Components ---

const FeatureCard = ({ icon: Icon, title, desc, color, variants }: { icon: any, title: string, desc: string, color: string, variants?: any }) => {
    const colorClasses: {[key: string]: string} = {
        blue: 'text-blue-400 group-hover:bg-blue-400/10 group-hover:border-blue-400/50',
        amber: 'text-amber-400 group-hover:bg-amber-400/10 group-hover:border-amber-400/50',
        emerald: 'text-emerald-400 group-hover:bg-emerald-400/10 group-hover:border-emerald-400/50',
        red: 'text-red-400 group-hover:bg-red-400/10 group-hover:border-red-400/50',
        purple: 'text-purple-400 group-hover:bg-purple-400/10 group-hover:border-purple-400/50',
        cyan: 'text-cyan-400 group-hover:bg-cyan-400/10 group-hover:border-cyan-400/50',
    };

    return (
        <motion.div 
            variants={variants}
            whileHover={{ y: -5 }}
            className="bg-slate-900/50 backdrop-blur-sm p-8 rounded-2xl border border-slate-800 hover:border-opacity-100 hover:shadow-[0_0_30px_rgba(0,0,0,0.2)] transition-all duration-300 group"
        >
            <div className={`w-12 h-12 rounded-xl flex items-center justify-center mb-6 bg-slate-950 border border-slate-800 transition-colors duration-300 ${colorClasses[color]}`}>
                <Icon className="w-6 h-6" />
            </div>
            <h3 className="text-xl font-bold text-white mb-3">{title}</h3>
            <p className="text-slate-400 leading-relaxed text-sm">{desc}</p>
        </motion.div>
    );
};

const MarketIntelligenceSection = () => {
    const [activeRegion, setActiveRegion] = useState(0);
    
    const regions = [
        { id: 'lisbon', name: 'Lisbon', price: 5422, yield: 4.8, trend: 3.2, label: 'Premium' },
        { id: 'porto', name: 'Porto', price: 3680, yield: 5.6, trend: 5.1, label: 'Growth' },
        { id: 'algarve', name: 'Algarve', price: 3850, yield: 6.1, trend: 2.8, label: 'Seasonal' },
    ];

    useEffect(() => {
        const interval = setInterval(() => {
            setActiveRegion(prev => (prev + 1) % regions.length);
        }, 4000);
        return () => clearInterval(interval);
    }, []);

    const currentData = regions[activeRegion];

    // Animation variants for this section
    const fadeInUp: Variants = {
        hidden: { opacity: 0, y: 40 },
        visible: { 
            opacity: 1, 
            y: 0, 
            transition: { duration: 0.8, ease: "easeOut" }
        }
    };

    const slideInRight: Variants = {
        hidden: { opacity: 0, x: 50 },
        visible: { 
            opacity: 1, 
            x: 0,
            transition: { duration: 0.8, ease: "easeOut", delay: 0.2 }
        }
    };

    return (
        <section id="market-data" className="py-24 border-y border-slate-900 bg-slate-900/30 relative overflow-hidden">
            <motion.div 
                initial="hidden"
                whileInView="visible"
                viewport={{ once: false, margin: "-100px" }}
                className="max-w-7xl mx-auto px-6 relative z-10"
            >
                <div className="flex flex-col lg:flex-row items-center justify-between gap-16">
                    
                    {/* Left Text Content */}
                    <motion.div variants={fadeInUp} className="max-w-xl">
                        <div className="flex items-center gap-2 text-emerald-400 font-bold uppercase tracking-widest text-xs mb-6">
                            <Globe className="w-4 h-4" /> Market Intelligence • Q1 2025
                        </div>
                        <h3 className="text-4xl font-black text-white mb-6 tracking-tight leading-tight">
                            Master the Portuguese<br/> Real Estate Landscape
                        </h3>
                        <p className="text-slate-400 mb-8 text-lg leading-relaxed">
                            Visualize yields, pricing trends, and tax impacts from Lisbon to the Algarve.
                        </p>
                        
                        {/* Data Grid */}
                        <div className="grid grid-cols-2 gap-4">
                            <div className="bg-slate-950/80 p-5 rounded-2xl border border-slate-800 hover:border-emerald-500/30 transition-colors group">
                                <div className="flex justify-between items-start mb-2">
                                    <p className="text-xs text-slate-500 uppercase font-bold">Avg. Gross Yield</p>
                                    <TrendingUp className="w-4 h-4 text-emerald-500 group-hover:scale-110 transition-transform" />
                                </div>
                                <p className="text-3xl font-black text-white">5.4%</p>
                                <p className="text-xs text-emerald-400 mt-1 font-mono">+0.2% National Avg</p>
                            </div>
                            
                            <div className="bg-slate-950/80 p-5 rounded-2xl border border-slate-800 hover:border-blue-500/30 transition-colors group">
                                <div className="flex justify-between items-start mb-2">
                                    <p className="text-xs text-slate-500 uppercase font-bold">Price Growth (YoY)</p>
                                    <ArrowUpRight className="w-4 h-4 text-blue-500 group-hover:scale-110 transition-transform" />
                                </div>
                                <p className="text-3xl font-black text-white">8.2%</p>
                                <p className="text-xs text-blue-400 mt-1 font-mono">Strong Demand</p>
                            </div>
                        </div>

                        <div className="mt-8 flex items-center gap-2 text-sm text-slate-500">
                            <CheckCircle2 className="w-4 h-4 text-emerald-500" />
                            <span>Updated for 2024/25 Tax Rules (Mais-Valias)</span>
                        </div>
                    </motion.div>

                    {/* Right Widget - Interactive Map Terminal */}
                    <motion.div variants={slideInRight} className="flex-1 w-full max-w-2xl">
                         <div className="bg-slate-950 rounded-3xl border border-slate-800 shadow-2xl overflow-hidden relative flex flex-col">
                            {/* Terminal Header */}
                            <div className="h-12 bg-slate-900 border-b border-slate-800 flex items-center justify-between px-6 shrink-0">
                                <div className="flex items-center gap-2 text-xs font-mono text-emerald-500">
                                    <Activity className="w-4 h-4 animate-pulse" />
                                    LIVE CONNECTION ESTABLISHED
                                </div>
                                <div className="flex gap-1.5">
                                    <div className="w-2 h-2 rounded-full bg-slate-700"></div>
                                    <div className="w-2 h-2 rounded-full bg-slate-700"></div>
                                </div>
                            </div>

                            <div className="p-6 grid md:grid-cols-2 gap-6 grow h-full min-h-[360px]">
                                {/* Map Visualization */}
                                <div className="relative bg-slate-900/50 rounded-2xl border border-slate-800/50 p-4 flex items-center justify-center group overflow-hidden">
                                     {/* Improved SVG ViewBox to prevent clipping */}
                                     <svg viewBox="-20 -20 140 240" preserveAspectRatio="xMidYMid meet" className="w-full h-full drop-shadow-2xl filter">
                                        {/* Portugal Map Path - Simplified but recognizable */}
                                        <path d="M25,10 L45,5 L65,10 L85,30 L75,60 L85,90 L65,130 L75,160 L45,200 L15,160 L5,100 L25,50 Z" 
                                              fill="#1e293b" 
                                              stroke="#334155" 
                                              strokeWidth="2" 
                                              strokeLinejoin="round"
                                              vectorEffect="non-scaling-stroke"
                                        />
                                        
                                        {/* Active Region Pins */}
                                        <g className={`transition-all duration-500 ${activeRegion === 1 ? 'opacity-100' : 'opacity-40'}`}>
                                            <circle cx="35" cy="40" r="4" className="fill-emerald-500" />
                                            <circle cx="35" cy="40" r="10" className="fill-emerald-500/20 animate-ping" />
                                        </g>
                                        
                                        <g className={`transition-all duration-500 ${activeRegion === 0 ? 'opacity-100' : 'opacity-40'}`}>
                                            <circle cx="25" cy="110" r="4" className="fill-emerald-500" />
                                            <circle cx="25" cy="110" r="10" className="fill-emerald-500/20 animate-ping" />
                                        </g>

                                        <g className={`transition-all duration-500 ${activeRegion === 2 ? 'opacity-100' : 'opacity-40'}`}>
                                            <circle cx="50" cy="175" r="4" className="fill-emerald-500" />
                                            <circle cx="50" cy="175" r="10" className="fill-emerald-500/20 animate-ping" />
                                        </g>
                                     </svg>

                                     {/* Grid Overlay */}
                                     <div className="absolute inset-0 bg-[linear-gradient(rgba(30,41,59,0.2)_1px,transparent_1px),linear-gradient(90deg,rgba(30,41,59,0.2)_1px,transparent_1px)] bg-[size:20px_20px] pointer-events-none"></div>
                                </div>

                                {/* Dynamic Data Feed */}
                                <div className="flex flex-col justify-between">
                                    <div>
                                        <p className="text-xs font-bold text-slate-500 uppercase tracking-widest mb-4">Regional Analysis</p>
                                        
                                        {/* Active Region Stats */}
                                        <div className="animate-in fade-in slide-in-from-right-4 duration-500 key={currentData.id}">
                                            <div className="flex justify-between items-center mb-2">
                                                <h4 className="text-2xl font-bold text-white">{currentData.name}</h4>
                                                <span className="px-2 py-0.5 rounded bg-slate-800 border border-slate-700 text-[10px] font-bold uppercase text-slate-300">
                                                    {currentData.label}
                                                </span>
                                            </div>
                                            <div className="space-y-4 mt-4">
                                                <div className="flex justify-between border-b border-slate-800 pb-2">
                                                    <span className="text-sm text-slate-400">Avg. Price</span>
                                                    <span className="text-sm font-mono font-bold text-white">€{currentData.price.toLocaleString()}/m²</span>
                                                </div>
                                                <div className="flex justify-between border-b border-slate-800 pb-2">
                                                    <span className="text-sm text-slate-400">Est. Yield</span>
                                                    <span className="text-sm font-mono font-bold text-emerald-400">{currentData.yield}%</span>
                                                </div>
                                                <div className="flex justify-between pb-2">
                                                    <span className="text-sm text-slate-400">Trend (Q1)</span>
                                                    <span className="text-sm font-mono font-bold text-emerald-400">+{currentData.trend}%</span>
                                                </div>
                                            </div>
                                        </div>
                                    </div>

                                    {/* Mini Ticker */}
                                    <div className="bg-slate-900 rounded-xl p-3 border border-slate-800">
                                        <div className="flex items-center gap-2 mb-2">
                                            <div className="w-1.5 h-1.5 rounded-full bg-red-500 animate-pulse"></div>
                                            <span className="text-[10px] font-bold uppercase text-slate-500">Live Feed</span>
                                        </div>
                                        <div className="h-12 overflow-hidden relative">
                                            <div className="absolute w-full animate-[translate-y_-50%] space-y-2">
                                                <p className="text-xs font-mono text-slate-400 truncate">
                                                    <span className="text-emerald-500">BUY</span> T2 Apartment • Lisbon • €420k
                                                </p>
                                                <p className="text-xs font-mono text-slate-400 truncate">
                                                    <span className="text-blue-500">RENT</span> Villa • Algarve • €2.8k/mo
                                                </p>
                                                <p className="text-xs font-mono text-slate-400 truncate">
                                                    <span className="text-emerald-500">BUY</span> Duplex • Porto • €310k
                                                </p>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </motion.div>
                </div>
            </motion.div>
        </section>
    );
};

const PricingSection = ({ onNavigateToAuth }: { onNavigateToAuth: () => void }) => {
    const [isAnnual, setIsAnnual] = useState(true);

    const plans = [
        {
            name: "Starter",
            price: "Free",
            description: "Perfect for first-time investors.",
            features: ["Track 1 Property", "Basic Analytics", "Tax Calculator Access", "Manual Data Entry"],
            highlight: false,
            cta: "Start Free"
        },
        {
            name: "Investor",
            price: isAnnual ? "€12" : "€15",
            period: "/mo",
            description: "Automate your growing portfolio.",
            features: ["Up to 10 Properties", "AI Advisor (Gemini)", "Document Storage", "Smart Alerts", "Deal Analyzer"],
            highlight: true,
            cta: "Go Professional"
        },
        {
            name: "Pro",
            price: isAnnual ? "€39" : "€49",
            period: "/mo",
            description: "For funds and power users.",
            features: ["Unlimited Units", "Advanced Reporting", "Priority Support", "API Access", "White-label Exports"],
            highlight: false,
            cta: "Contact Sales"
        }
    ];

    return (
        <section id="pricing" className="py-24 bg-slate-950/80 relative overflow-hidden border-t border-slate-900">
            <div className="max-w-7xl mx-auto px-6 relative z-10">
                <div className="text-center max-w-3xl mx-auto mb-16">
                    <motion.h2 
                        initial={{ opacity: 0, y: 20 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: false }}
                        className="text-3xl md:text-5xl font-black text-white mb-6 tracking-tight"
                    >
                        Choose Your <br />
                        <span className="text-transparent bg-clip-text bg-gradient-to-r from-blue-400 to-emerald-400">Command Center</span>
                    </motion.h2>
                    <motion.p 
                        initial={{ opacity: 0, y: 20 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: false }}
                        transition={{ delay: 0.1 }}
                        className="text-lg text-slate-400 mb-10"
                    >
                        Whether you're tracking one property or building a portfolio, DC PropChain scales with you.
                    </motion.p>

                    {/* Toggle */}
                    <motion.div 
                        initial={{ opacity: 0, scale: 0.9 }}
                        whileInView={{ opacity: 1, scale: 1 }}
                        className="flex items-center justify-center gap-4"
                    >
                        <span className={`text-sm font-bold ${!isAnnual ? 'text-white' : 'text-slate-500'}`}>Monthly</span>
                        <button 
                            onClick={() => setIsAnnual(!isAnnual)}
                            className="w-14 h-8 bg-slate-800 rounded-full p-1 relative transition-colors border border-slate-700"
                        >
                            <div className={`w-6 h-6 bg-emerald-500 rounded-full shadow-lg transform transition-transform duration-300 ${isAnnual ? 'translate-x-6' : 'translate-x-0'}`}></div>
                        </button>
                        <span className={`text-sm font-bold ${isAnnual ? 'text-white' : 'text-slate-500'}`}>
                            Annual <span className="text-emerald-400 text-[10px] bg-emerald-500/10 border border-emerald-500/20 px-1.5 py-0.5 rounded ml-1">-20%</span>
                        </span>
                    </motion.div>
                </div>

                <div className="grid md:grid-cols-3 gap-8">
                    {plans.map((plan, idx) => (
                        <motion.div
                            key={idx}
                            initial={{ opacity: 0, y: 30 }}
                            whileInView={{ opacity: 1, y: 0 }}
                            viewport={{ once: false }}
                            transition={{ delay: idx * 0.1 }}
                            whileHover={{ y: -10 }}
                            className={`relative p-8 rounded-3xl border transition-all duration-300 ${
                                plan.highlight 
                                ? 'bg-slate-900/80 border-emerald-500/50 shadow-[0_0_30px_rgba(16,185,129,0.1)]' 
                                : 'bg-slate-950 border-slate-800 hover:border-slate-700'
                            }`}
                        >
                            {plan.highlight && (
                                <div className="absolute top-0 left-1/2 -translate-x-1/2 -translate-y-1/2 bg-emerald-500 text-slate-950 text-xs font-bold px-3 py-1 rounded-full shadow-lg border border-emerald-400">
                                    MOST POPULAR
                                </div>
                            )}
                            
                            <div className="mb-8">
                                <h3 className="text-lg font-bold text-white mb-2">{plan.name}</h3>
                                <div className="flex items-end gap-1 mb-2">
                                    <span className="text-4xl font-black text-white">{plan.price}</span>
                                    {plan.period && <span className="text-slate-500 mb-1 font-medium">{plan.period}</span>}
                                </div>
                                <p className="text-sm text-slate-400">{plan.description}</p>
                            </div>

                            <ul className="space-y-4 mb-8">
                                {plan.features.map((feature, fIdx) => (
                                    <li key={fIdx} className="flex items-start gap-3 text-sm text-slate-300">
                                        <CheckCircle2 className={`w-5 h-5 flex-shrink-0 ${plan.highlight ? 'text-emerald-400' : 'text-slate-500'}`} />
                                        <span>{feature}</span>
                                    </li>
                                ))}
                            </ul>

                            <button 
                                onClick={onNavigateToAuth}
                                className={`w-full py-4 rounded-xl font-bold transition-all ${
                                    plan.highlight
                                    ? 'bg-emerald-600 text-white hover:bg-emerald-500 shadow-lg shadow-emerald-900/20'
                                    : 'bg-slate-800 text-white hover:bg-slate-700'
                                }`}
                            >
                                {plan.cta}
                            </button>
                        </motion.div>
                    ))}
                </div>
            </div>
        </section>
    );
};

export default LandingPage;
